import { useCallback } from "react";
import { useNavigate } from "react-router";
import { omit, pick } from "lodash";

import { yupResolver } from "@hookform/resolvers/yup";

import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";

import { FORM_ERROR_DELAY_IN_MS } from "@covertree/consts";
import { useFormWithNotification } from "@covertree/hooks";

import { useAppDispatch } from "@redux/hooks";
import { navigateToPolicyStep, NewPolicyRoutes } from "@routing/paths";

import {
  personalDetailsFormSchema,
  PersonalDetailsWithExtraQuestionsForm,
} from "@covertree/new-business-forms";
import {
  createOrUpdateBasicPolicyDetailsWithHolder,
  selectExtraPolicyDetails,
  selectIsBookConversion,
  selectPersonalDetailsFormDefaultValues,
  useNewBusinessSelector,
} from "@covertree/new-business-redux";

import { PersonalDetailsView } from "./PersonalDetailsView";

export function PersonalDetailsStep() {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const personalDetailsDefaultValues = useNewBusinessSelector(
    selectPersonalDetailsFormDefaultValues
  );
  const extraQuestionsDefaultValues = useNewBusinessSelector(
    selectExtraPolicyDetails
  );
  const isBookConversion = useNewBusinessSelector(selectIsBookConversion);

  const form = useFormWithNotification<PersonalDetailsWithExtraQuestionsForm>({
    mode: "all",
    defaultValues: {
      ...personalDetailsDefaultValues,
      ...extraQuestionsDefaultValues,
    },
    delayError: FORM_ERROR_DELAY_IN_MS,
    reValidateMode: "onSubmit",
    // eslint-disable-next-line
    // @ts-expect-error
    resolver: yupResolver(personalDetailsFormSchema(isBookConversion)),
  });

  const savePersonalDetails = useCallback(
    async (formValues: PersonalDetailsWithExtraQuestionsForm) => {
      try {
        // showLoadingFrame is inside of createOrUpdateBasicPolicyDetailsWithHolder reducer
        await dispatch(
          createOrUpdateBasicPolicyDetailsWithHolder({
            personalDetails: omit(formValues, [
              "noEmail",
              "hasAutoPolicy",
              "physicallyMailDocuments",
              "hasAnotherCoverTreePolicy",
            ]),
            extraQuestions: pick(formValues, [
              "noEmail",
              "hasAutoPolicy",
              "physicallyMailDocuments",
              "hasAnotherCoverTreePolicy",
            ]),
          })
        ).unwrap();
        navigateToPolicyStep(
          navigate,
          NewPolicyRoutes.PERSONALIZED_QUOTES,
          NewPolicyRoutes.PERSONAL_DETAILS,
          dispatch
        );
      } catch (e) {
        console.error(e);
        navigateToPolicyStep(
          navigate,
          NewPolicyRoutes.ERROR,
          NewPolicyRoutes.PERSONAL_DETAILS,
          dispatch
        );
      }
    },
    [dispatch]
  );

  return (
    <Grid item xs={12}>
      <Typography variant="h4">Personal Details</Typography>
      <PersonalDetailsView form={form} onSubmit={savePersonalDetails} />
    </Grid>
  );
}
